// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import OrderActions from "@store/Library/Components/Toolkit/Order/OrderActions";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { baseGridProps } from "@store/Themes/common/props/baseGrid";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentCartState } from "@toolkit/Framework/Store/Data/Carts/CartsHooks";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import * as React from "react";
import { css } from "styled-components";

function CartLines(props: any) {
    return createWidgetElement("Cart/CartLines", {
        ...props,
        forAddToCartModal: true,
    });
}

function ProductCarousel() {
    return createWidgetElement("Common/ProductCarousel", {
        fields: {
            titleAlignment: "center",
            carouselType: "recommendedProducts",
            maxNumberOfColumns: 3,
            widgetPosition: 1,
            showTextBadges: true,
            showImage: true,
            showTitle: true,
            showBrandName: true,
            showPartNumbers: true,
            showAvailability: true,
            showPrice: true,
            showAddToCart: true,
            showAddToList: true,
            maximumNumberOfTextBadges: "4",
            inModal: true,
        },
    });
}

interface OwnProps {
    modalIsOpen: boolean;
    onClose: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    returnedCartLine?: CartLineModel[];
    extendedStyles?: AddToCartModalStyles;
}

type Props = OwnProps & HasHistory;

export interface AddToCartModalStyles {
    modal?: ModalPresentationProps;
    itemAddedToCartBox?: InfosBoxProps;
    loadingOverlay?: LoadingOverlayProps;
    container?: GridContainerProps;
    mainColumn?: GridItemProps;
    sideColumnMobile?: GridItemProps;
    sideColumnMobileWrapper?: InjectableCss;
    sideColumnDesktop?: GridItemProps;
    sideColumnWrapper?: InjectableCss;
    proceedToCartButton?: ButtonPresentationProps;
    continueToShopButton?: ButtonPresentationProps;
}

/* Store Sync - Styles: starter > Library > Components > Toolkit > Layout > OrderLayout */
export const addToCartModalStyles: AddToCartModalStyles = {
    modal: {
        sizeVariant: "large",
    },
    itemAddedToCartBox: { type: "success" },
    loadingOverlay: { minHeight: 150 },
    container: baseGridProps.container,
    mainColumn: {
        width: [12, 12, 7, 8, 9],
        printWidth: 12,
    },
    sideColumnMobile: {
        width: [12, 12, 0, 0, 0],
        printWidth: 0,
    },
    sideColumnMobileWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    sideColumnDesktop: {
        width: [0, 0, 5, 4, 3],
        printWidth: 12,
    },
    sideColumnWrapper: {
        css: css`
            position: sticky;
            top: ${pxToRem(20)};
            width: 100%;
        `,
    },
    proceedToCartButton: { variant: "primary" },
    continueToShopButton: { variant: "secondary" },
};

const StyledSection = getStyledWrapper("section");

const AddToCartModal: React.FunctionComponent<Props> = props => {
    const cartPageLink = usePageLinkByPageType(constants.pageType.cart);
    const [styles] = React.useState(() => mergeToNew(addToCartModalStyles, props.extendedStyles));

    const handleProceedToCart = () => {
        if (cartPageLink) {
            props.history.push(cartPageLink.url);
        }
    };

    const ModalButtons = () => (
        <OrderActions>
            <Button
                {...styles.proceedToCartButton}
                onClick={e => {
                    props.onClose(e);
                    handleProceedToCart();
                }}
            >
                {translate("Proceed to cart")}
            </Button>
            <Button {...styles.continueToShopButton} onClick={props.onClose}>
                {translate("Continue Shopping")}
            </Button>
        </OrderActions>
    );

    return (
        <Modal
            headline={translate("Cart Recommendations")}
            {...styles.modal}
            isOpen={props.modalIsOpen}
            handleClose={props.onClose}
        >
            <StyledSection>
                <InfosBox {...styles.itemAddedToCartBox}>
                    <ProductAddedToCartMessage isQtyAdjusted={false} multipleProducts={false} />
                </InfosBox>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.sideColumnMobile}>
                        <StyledWrapper {...styles.sideColumnMobileWrapper}>
                            <ModalButtons />
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.mainColumn}>
                        <CartLines returnedCartLine={props.returnedCartLine} />
                    </GridItem>
                    <GridItem {...styles.sideColumnDesktop}>
                        <StyledWrapper {...styles.sideColumnWrapper}>
                            <ModalButtons />
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
                <ProductCarousel />
            </StyledSection>
        </Modal>
    );
};

export default withHistory(AddToCartModal);
